import React, { useState, useEffect } from 'react';
import { CharacterCreationData } from './CharacterCreationWizard';
import { EnhancedClass } from '../../types/class-data';
import { findClassById } from '../../utils/classDataLoader';
import './StartingChoices.css';

interface StartingChoicesProps {
  characterData: CharacterCreationData;
  onDataChange: (data: Partial<CharacterCreationData>) => void;
}

const StartingChoices: React.FC<StartingChoicesProps> = ({
  characterData,
  onDataChange,
}) => {
  const [classData, setClassData] = useState<EnhancedClass | null>(null);
  const [selections, setSelections] = useState<Record<string, string>>(
    characterData.startingChoices || {}
  );
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadClassData = async () => {
      if (characterData.class) {
        try {
          const data = await findClassById(characterData.class);
          setClassData(data);
        } catch (error) {
          console.error('Failed to load class data for starting choices:', error);
        }
      }
      setLoading(false);
    };

    loadClassData();
  }, [characterData.class]);

  const handleChoiceSelect = (category: string, option: string) => {
    const newSelections = { ...selections, [category]: option };
    setSelections(newSelections);
    onDataChange({ startingChoices: newSelections });
  };

  if (loading) {
    return <div className="starting-choices__loading">Loading starting choices...</div>;
  }

  if (!classData || !classData.startingChoices || classData.startingChoices.length === 0) {
    return (
      <div className="starting-choices__empty">
        <p>This class doesn't have any starting choices to make.</p>
        <p>You can proceed to the next step.</p>
      </div>
    );
  }

  const allChoicesMade = classData.startingChoices.every(
    choice => selections[choice.category]
  );

  return (
    <div className="starting-choices">
      <p className="starting-choices__description">
        Your class provides some starting choices to customize your character. 
        Make your selections from the options below.
      </p>

      <div className="starting-choices__content">
        {classData.startingChoices.map((choice, index) => (
          <div key={index} className="choice-section">
            <h3 className="choice-section__title">
              Choose one {choice.category}:
            </h3>
            
            <div className="choice-options">
              {choice.options.map((option, optionIndex) => (
                <div
                  key={optionIndex}
                  className={`choice-option ${
                    selections[choice.category] === option ? 'choice-option--selected' : ''
                  }`}
                  onClick={() => handleChoiceSelect(choice.category, option)}
                >
                  <div className="choice-option__text">{option}</div>
                </div>
              ))}
            </div>
          </div>
        ))}

        {allChoicesMade && (
          <div className="starting-choices__summary">
            <h3>Your Selections</h3>
            <div className="selections-summary">
              {Object.entries(selections).map(([category, selection]) => (
                <div key={category} className="selection-item">
                  <strong>{category.charAt(0).toUpperCase() + category.slice(1)}:</strong> {selection}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StartingChoices;